/**
 * Product / buy page — one report, birth-data form, coupon, pay button.
 *
 * Form fields:
 *  - Buyer block: name, email, phone (optional)
 *  - Chart block: name, dob, tob, place — duplicated for the two-chart SKUs.
 *  - Coupon (optional) with live validation.
 *
 * On submit:
 *  1. POST /storefront/orders with {report_slug, buyer, chart_request, coupon_code, lang}
 *  2. If is_free → navigate straight to /thank-you/<token>
 *  3. Else → navigate to /checkout/<order_id> (with state) which opens Razorpay
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLang } from '@/i18n/useLang';
import { StorefrontError, storefrontApi } from '@/api/storefront';
import type {
  CouponValidateResponse,
  CreateOrderRequest,
  InputShape,
  SkuEntry,
} from '@/api/storefront';
import { PlacePicker, type PlacePick } from '@/components/PlacePicker';
import { StorefrontHeader } from '@/components/storefront/StorefrontHeader';

interface ChartFields {
  name: string;
  dob: string;        // yyyy-mm-dd
  tob: string;        // HH:MM (24h) — we'll add :00 on submit
  place: PlacePick | null;  // resolved via Nominatim before checkout
}

const EMPTY_CHART: ChartFields = { name: '', dob: '', tob: '', place: null };

export function ProductPage() {
  const { reportSlug } = useParams<{ reportSlug: string }>();
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const cat = useQuery({
    queryKey: ['storefront', 'catalogue'],
    queryFn: () => storefrontApi.catalogue(),
    staleTime: 60_000,
  });
  const sku: SkuEntry | undefined = cat.data?.skus.find((s) => s.slug === reportSlug);

  // Form state
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [chartA, setChartA] = useState<ChartFields>(EMPTY_CHART);
  const [chartB, setChartB] = useState<ChartFields>(EMPTY_CHART);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidateResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Default Chart A name to buyer name to save a keystroke
  useEffect(() => {
    if (buyerName && !chartA.name) {
      setChartA((c) => ({ ...c, name: buyerName }));
    }
  }, [buyerName, chartA.name]);

  const validateCouponMut = useMutation({
    mutationFn: async () => {
      if (!sku) throw new Error('no sku');
      return storefrontApi.validateCoupon(couponCode.trim(), sku.slug);
    },
    onSuccess: (res) => {
      if (res.valid) setAppliedCoupon(res);
      else setAppliedCoupon(res); // store the rejection for display
    },
  });

  const createOrderMut = useMutation({
    mutationFn: async () => {
      if (!sku) throw new Error('no sku');
      const chart_request = buildChartRequest(sku.input_shape, chartA, chartB);
      const req: CreateOrderRequest = {
        report_slug: sku.slug,
        buyer: {
          name: buyerName.trim(),
          email: buyerEmail.trim(),
          phone: buyerPhone.trim() || null,
        },
        chart_request,
        coupon_code: appliedCoupon?.valid ? appliedCoupon.code : null,
        lang,
      };
      return storefrontApi.createOrder(req);
    },
    onSuccess: (res) => {
      if (res.is_free) {
        navigate(`/thank-you/${res.public_token}`, {
          state: { freshOrder: res, isFree: true },
        });
      } else {
        navigate(`/checkout/${res.order_id}`, {
          state: { order: res },
        });
      }
    },
    onError: (err) => {
      if (err instanceof StorefrontError) setSubmitError(err.detail);
      else setSubmitError(String(err));
    },
  });

  if (cat.isLoading) {
    return (
      <>
        <StorefrontHeader />
        <main className="mx-auto max-w-screen-md px-4 py-10 text-center text-sm text-stone-500">
          {t('लोड हो रहा है…', 'Loading…')}
        </main>
      </>
    );
  }
  if (!sku) {
    return (
      <>
        <StorefrontHeader />
        <main className="mx-auto max-w-screen-md px-4 py-10 text-center">
          <p className="text-sm text-rose-300">
            {t('यह रिपोर्ट उपलब्ध नहीं है।', 'This report is not available.')}
          </p>
        </main>
      </>
    );
  }

  const formReady = isFormReady(sku.input_shape, buyerName, buyerEmail, chartA, chartB);
  const finalPriceDisplay =
    appliedCoupon?.valid ? appliedCoupon.final_price_inr_display : sku.price_inr_display;

  return (
    <>
      <StorefrontHeader />
      <main className="mx-auto max-w-screen-md px-4 py-8 lg:py-12">
        <div className="mb-6 rounded-2xl bg-stone-900/60 p-5 ring-1 ring-stone-800">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h1 className="text-xl font-bold text-saffron-100 lg:text-2xl">
                {sku.emoji} {t(sku.title_hi, sku.title_en)}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-stone-400">
                {t(sku.blurb_hi, sku.blurb_en)}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-saffron-100">
                {finalPriceDisplay}
              </div>
              {appliedCoupon?.valid && appliedCoupon.discount_paise > 0 && (
                <div className="text-xs text-stone-500 line-through">
                  {sku.price_inr_display}
                </div>
              )}
            </div>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitError(null);
            void createOrderMut.mutateAsync();
          }}
          className="space-y-6"
        >
          <Section title={t('आपकी जानकारी', 'Your details')}>
            <Field id="buyer-name" label={t('पूरा नाम', 'Full name')}
                   value={buyerName} onChange={setBuyerName} required />
            <Field id="buyer-email" label={t('ईमेल', 'Email')} type="email"
                   value={buyerEmail} onChange={setBuyerEmail} required />
            <Field id="buyer-phone" label={t('फ़ोन (वैकल्पिक)', 'Phone (optional)')}
                   value={buyerPhone} onChange={setBuyerPhone} />
            <p className="text-xs text-stone-500">
              {t(
                'रिपोर्ट इसी ईमेल पर भेजी जाएगी।',
                'The PDF will be emailed to this address.',
              )}
            </p>
          </Section>

          <Section title={
            sku.input_shape === 'two_chart'
              ? t('प्रथम व्यक्ति का जन्म-विवरण', "Person A's birth details")
              : t('जन्म-विवरण', 'Birth details')
          }>
            <ChartFieldset fields={chartA} onChange={setChartA} />
          </Section>

          {sku.input_shape === 'two_chart' && (
            <Section title={t('द्वितीय व्यक्ति का जन्म-विवरण', "Person B's birth details")}>
              <ChartFieldset fields={chartB} onChange={setChartB} />
            </Section>
          )}

          <Section title={t('कूपन कोड (वैकल्पिक)', 'Coupon code (optional)')}>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value);
                  setAppliedCoupon(null);
                }}
                placeholder={t('कोड दर्ज करें', 'Enter code')}
                className="flex-1 rounded-md border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100 outline-none focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500"
              />
              <button
                type="button"
                onClick={() => void validateCouponMut.mutateAsync()}
                disabled={!couponCode.trim() || validateCouponMut.isPending}
                className="rounded-md border border-saffron-600 bg-stone-950 px-3 py-2 text-sm text-saffron-200 hover:bg-saffron-700/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('लागू करें', 'Apply')}
              </button>
            </div>
            {appliedCoupon && (
              <div
                role="status"
                className={`mt-2 rounded-md px-3 py-2 text-sm ring-1 ${
                  appliedCoupon.valid
                    ? 'bg-emerald-950/50 text-emerald-200 ring-emerald-900'
                    : 'bg-rose-950/50 text-rose-200 ring-rose-900'
                }`}
              >
                {appliedCoupon.valid ? (
                  <>
                    {t('कूपन लागू!', 'Coupon applied!')}{' '}
                    {appliedCoupon.is_free
                      ? t('रिपोर्ट निःशुल्क मिलेगी।', 'Your report is free.')
                      : t(
                          `नई कीमत: ${appliedCoupon.final_price_inr_display}`,
                          `New total: ${appliedCoupon.final_price_inr_display}`,
                        )}
                    {appliedCoupon.owner_display_name && (
                      <span className="ml-2 text-xs opacity-75">
                        ({appliedCoupon.owner_display_name})
                      </span>
                    )}
                  </>
                ) : (
                  humaniseCouponError(appliedCoupon.error, lang)
                )}
              </div>
            )}
          </Section>

          {submitError && (
            <div role="alert" className="rounded-md bg-rose-950/60 px-3 py-2 text-sm text-rose-200 ring-1 ring-rose-900">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={!formReady || createOrderMut.isPending}
            className="w-full rounded-md bg-saffron-600 px-4 py-3 text-base font-semibold text-stone-950 transition hover:bg-saffron-500 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-500"
          >
            {createOrderMut.isPending
              ? t('आगे बढ़ रहे हैं…', 'Continuing…')
              : (sku.price_paise === 0 || (appliedCoupon?.valid && appliedCoupon.is_free))
                ? t('निःशुल्क रिपोर्ट प्राप्त करें', 'Get free report')
                : t(`${finalPriceDisplay} का भुगतान करें`, `Pay ${finalPriceDisplay}`)}
          </button>

          <p className="text-center text-xs text-stone-500">
            {t(
              'Razorpay द्वारा सुरक्षित भुगतान — कार्ड, UPI, नेट-बैंकिंग, वॉलेट।',
              'Secure payments by Razorpay — Card, UPI, Netbanking, Wallets.',
            )}
          </p>
        </form>
      </main>
    </>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

function buildChartRequest(
  shape: InputShape,
  a: ChartFields,
  b: ChartFields,
): Record<string, unknown> {
  if (shape === 'two_chart') {
    // Compatibility uses {boy, girl}; business-partner uses {partner_a, partner_b}.
    // The backend's catalogue maps slug→endpoint; we always send a generic
    // payload with both shapes — the delivery webhook adapts as needed.
    return {
      boy: chartToPayload(a),
      girl: chartToPayload(b),
      partner_a: chartToPayload(a),
      partner_b: chartToPayload(b),
    };
  }
  return chartToPayload(a);
}

function chartToPayload(c: ChartFields): Record<string, unknown> {
  // Build the same payload our existing PDF endpoints accept. We
  // always send the resolved {lat, lon} pair (PlaceManual shape) so
  // the PDF endpoint never has to re-geocode at delivery time.
  const lat = c.place?.lat ?? 0;
  const lon = c.place?.lon ?? 0;
  return {
    name: c.name.trim() || 'Anonymous',
    dob: c.dob,
    tob: c.tob.length === 5 ? `${c.tob}:00` : c.tob,
    place: { lat, lon },
  };
}

function isFormReady(
  shape: InputShape,
  buyerName: string,
  buyerEmail: string,
  a: ChartFields,
  b: ChartFields,
): boolean {
  if (!buyerName.trim() || !buyerEmail.trim()) return false;
  if (!isChartReady(a)) return false;
  if (shape === 'two_chart' && !isChartReady(b)) return false;
  return true;
}

function isChartReady(c: ChartFields): boolean {
  return !!(c.name.trim() && c.dob && c.tob && c.place);
}

function humaniseCouponError(
  err: string | null,
  lang: 'hi' | 'en',
): string {
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  switch (err) {
    case 'unknown':
      return t('यह कूपन कोड मान्य नहीं है।', 'This coupon code is not recognised.');
    case 'inactive':
      return t('यह कूपन अब सक्रिय नहीं है।', 'This coupon is no longer active.');
    case 'expired':
      return t('कूपन की समय-सीमा समाप्त हो चुकी है।', 'This coupon has expired.');
    case 'exhausted':
      return t('कूपन का उपयोग पूर्ण हो चुका है।', 'This coupon has been fully used up.');
    case 'wrong_report':
      return t('यह कूपन इस रिपोर्ट पर लागू नहीं होता।',
               'This coupon does not apply to this report.');
    default:
      return t('कूपन अमान्य है।', 'Invalid coupon.');
  }
}

// ─── Primitive components ──────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl bg-stone-900/60 p-5 ring-1 ring-stone-800">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-saffron-400">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}

function Field({ id, label, value, onChange, type = 'text', required }: FieldProps) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-stone-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-md border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100 outline-none focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500"
      />
    </div>
  );
}

function ChartFieldset({
  fields,
  onChange,
}: {
  fields: ChartFields;
  onChange: (next: ChartFields) => void;
}) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  return (
    <>
      <Field id="c-name" label={t('नाम', 'Name')}
             value={fields.name}
             onChange={(v) => onChange({ ...fields, name: v })} required />
      <div className="grid grid-cols-2 gap-3">
        <Field id="c-dob" label={t('जन्म-तिथि', 'Date of birth')} type="date"
               value={fields.dob}
               onChange={(v) => onChange({ ...fields, dob: v })} required />
        <Field id="c-tob" label={t('जन्म-समय', 'Time of birth')} type="time"
               value={fields.tob}
               onChange={(v) => onChange({ ...fields, tob: v })} required />
      </div>
      <PlacePicker
        value={fields.place}
        onChange={(p) => onChange({ ...fields, place: p })}
        label={t('जन्म-स्थान (शहर, देश)', 'Place of birth (city, country)')}
        inputClassName="w-full rounded-md border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100 outline-none focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500"
        required
      />
    </>
  );
}
